import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import RatingV3 from './Rating';
import { ratingMatchTypeDisplay, ratingTypes } from '../../../utils/constants';

import styles from './ratingDisplayV3.scss';
import { getRatingDetails, setRatingChoice } from '../../../utils';

const RatingDisplayV3 = (props) => {
    const {
        isProProfile,
        profileData,
        ratingClassName,
        isOwnProfile,
        onTennisGetRatedClick,
        onPickleballGetRatedClick,
        doNotMask = false,
        'data-testid': dataTestId,
    } = props;

    const auth = useSelector(({ auth }) => auth);
    const userLoggedIn = auth?.user;
    const { isPowered } = userLoggedIn || {};
    const showTwoDecimals = profileData?.isPro || isOwnProfile;
    const { pbrRatingProgress } = profileData;

    const rating = getRatingDetails(setRatingChoice({ player: profileData }), isPowered, showTwoDecimals, false, false);
    const historicRatingData = props.profileData?.historicRatings;

    const getRatingDisplay = () => {
        const showGetRatedButton = userLoggedIn?.utrRangeRequired && !rating.showPBRRating && isOwnProfile;
        if (showGetRatedButton) {
            return (
                <RatingV3
                    title={`${ratingTypes.tennis} SGLS`}
                    titleColor="blue"
                    showGetRatedButton={true}
                    onGetRatedClick={onTennisGetRatedClick}
                    data-testid={dataTestId ? `${dataTestId}.tennis-singles-get-rated` : undefined}
                />
            );
        }

        if (rating.showPBRRating) {
            return (
                <RatingV3
                    title={ratingTypes.colorBall}
                    titleColor="blue"
                    ballColor={rating.pbrRating?.pbrBallColor}
                    value={rating.pbrRating?.ratingDisplay}
                    showProgressionBall={rating.pbrRating?.value}
                    footer={isOwnProfile && pbrRatingProgress ? pbrRatingProgress : ''}
                    showRatingInfo
                    data-testid={dataTestId ? `${dataTestId}.colorball` : undefined}
                />
            );
        }

        if (rating.status === 'Unrated' || rating.status === 'Inactive') {
            if (historicRatingData?.historicSinglesRating != null) {
                return (
                    <RatingV3
                        title={`${ratingTypes.tennis} SGLS`}
                        titleColor="blue"
                        value={historicRatingData?.historicSinglesRatingDisplay}
                        footer={`From ${moment(historicRatingData?.historicSinglesRatingDate).format('MMMM YYYY')}`}
                        data-testid={dataTestId ? `${dataTestId}.tennis-historic-singles` : undefined}
                    />
                );
            }
            if (historicRatingData?.historicDoublesRating != null) {
                return (
                    <RatingV3
                        title={`${ratingTypes.tennis} DBLS`}
                        titleColor="blue"
                        value={historicRatingData?.historicDoublesRatingDisplay}
                        footer={`From ${moment(historicRatingData?.historicDoublesRatingDate).format('MMMM YYYY')}`}
                        data-testid={dataTestId ? `${dataTestId}.tennis-historic-doubles` : undefined}
                    />
                );
            }
        }

        if ((rating.status === 'Unrated' || rating.status === 'Inactive') && rating.utrRange) {
            if (rating.utrRange.minUtr && rating.utrRange.maxUtr) {
                return (
                    <RatingV3
                        title={ratingTypes.tennis}
                        titleColor="blue"
                        value={`${doNotMask ? rating.utrRange.minUtr?.toFixed(2) : rating.utrRange.minUtrDisplay} - ${doNotMask ? rating.utrRange.maxUtr?.toFixed(2) : rating.utrRange.maxUtrDisplay}`}
                        footer={rating.status !== 'Unrated' && rating.status !== 'Inactive' && rating.progress ? `Reliab.: ${rating.progress}%` : 'Reliab.: Estimated'}
                        showRatingInfo
                        data-testid={dataTestId ? `${dataTestId}.tennis-range` : undefined}
                    />
                );
            }
            if (rating.utrRange.pbrRange && rating.showPBRRating) {
                return (
                    <div className={styles.utrRange} data-testid={dataTestId ? `${dataTestId}.utr-range-wrapper` : undefined}>
                        <RatingV3
                            title={ratingTypes.colorBall}
                            titleColor="blue"
                            ballColor={rating.utrRange.pbrRange.pbrBallColor}
                            value={rating.utrRange.pbrRange.value}
                            footer={`Reliab.: Estimated`}
                            showRatingInfo
                            data-testid={dataTestId ? `${dataTestId}.colorball-range` : undefined}
                        />
                    </div>
                );
            }
            if (!rating.utrRange.minUtr && (rating.utrRange.lastReliableRating || rating.utrRange.lastReliableRatingDisplay > 0)) {
                return (
                    <RatingV3
                        title={`${ratingTypes.tennis} SGLS`}
                        titleColor="blue"
                        value={doNotMask ? rating.utrRange.lastReliableRating.toFixed(2) : rating.utrRange.lastReliableRatingDisplay}
                        footer={`Last known (${moment(rating.utrRange.lastReliableRatingDate).format('M/DD/YYYY')})`}
                        isVerified={true}
                        showRatingInfo
                        data-testid={dataTestId ? `${dataTestId}.tennis-last-known` : undefined}
                    />
                );
            }
        }

        return (
            <RatingV3
                title={`${ratingTypes.tennis} ${ratingMatchTypeDisplay[rating.matchType]}`}
                titleColor="blue"
                // eslint-disable-next-line prefer-template
                value={`${rating.status === 'Unrated' ? 'Unrated' : rating.ratingNumber}${rating.ratingDecimal.length > 0 ? '.' + rating.ratingDecimal : ''}`}
                footer={rating.status !== 'Unrated' && rating.status !== 'Inactive' && rating.progress ? `Reliab.: ${rating.progress}%` : ''}
                isVerified={rating.verified && rating.status !== 'Unrated'}
                showRatingInfo
                data-testid={dataTestId ? `${dataTestId}.tennis-${rating.matchType || 'default'}` : undefined}
            />
        );
    };

    const showUTPRGetRatedButton = !rating.utrRange?.pkbRating && isOwnProfile;
    const hideUTPRRating = () => {
        if (rating.showPickleballRating) {
            return rating.utrRange?.pbrRange && rating.showPBRRating;
        }

        return true;
    };

    return (
        <div className={`${styles.ratingDisplay} ${ratingClassName || ''} updated`} data-testid={dataTestId}>
            <div className={`${styles.utrContainer} ${!userLoggedIn && !isProProfile ? styles.blur : ''}`}>
                {rating.showTennisRating && getRatingDisplay()}
                {!hideUTPRRating() && (
                    <RatingV3
                        title={ratingTypes.pickleball}
                        titleColor="green"
                        value={rating.pkbRating?.value}
                        showGetRatedButton={showUTPRGetRatedButton}
                        onGetRatedClick={onPickleballGetRatedClick}
                        showRatingInfo={!showUTPRGetRatedButton}
                        ratingInfoType="pickleball"
                        data-testid={dataTestId ? `${dataTestId}.pickleball` : undefined}
                    />
                )}
            </div>
            {!userLoggedIn && !isProProfile && (
                <div
                    className={`${styles.signUpToView} btn btn-primary text-uppercase`}
                    onClick={props.handleSignInClick}
                    data-testid={dataTestId ? `${dataTestId}.sign-in-btn` : undefined}
                >
                    Sign In to View Rating
                </div>
            )}
        </div>
    );
};

export default RatingDisplayV3;
